import React from 'react'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import utils from '../../../utils'

const InternalAssessmentExportExcel = ({
  data,
  departmentListV2,
  month,
  year,
}) => {
  // Helper function to strip HTML tags, preserve line breaks, and remove non-breaking spaces
  const stripHtml = html => {
    if (!html) return ''

    // Replace HTML entities and non-breaking spaces
    let text = html
      .replace(/&amp;/gi, '&') // Replace escaped &
      .replace(/&nbsp;/gi, ' ') // Replace &nbsp; with space
      .replace(/&/gi, '&') // Replace escaped &
      .replace(/ /gi, ' ') // Replace non-breaking space
      .replace(/\u00A0/g, ' ') // Replace Unicode non-breaking space
      .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n') // Extract <p> content, add \n
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n') // Add prefix for <li> and \n
      .replace(/<\/?(ul|ol)[^>]*>/gi, '') // Remove <ul> or <ol> tags
      .replace(/<br\s*\/?>/gi, '\n') // Replace <br> tags with \n
      .replace(/<[^>]+>/g, '') // Remove all other HTML tags
      .replace(/\s*\n\s*/g, '\n') // Normalize newlines
      .replace(/\n+/g, '\n') // Remove duplicate newlines
      .replace(/^\n|\n$/g, '') // Remove leading/trailing newlines
      .trim()

    return text
  }

  const handleExport = async () => {
    // B1: Tạo mapping theo từng phòng ban và từng user dựa trên evaluateeName
    const userDeptComments = {}

    // Duyệt qua từng evaluation trong data
    data.forEach(evaluation => {
      if (evaluation.status === 'COMPLETED') {
        // Tìm các bản ghi detail cho các evaluatorType
        const selfDetail = evaluation.details.find(
          detail => detail.evaluatorType === 'self'
        )
        const managerDetail = evaluation.details.find(
          detail => detail.evaluatorType === 'manager'
        )
        const editorDetail = evaluation.details.find(
          detail => detail.evaluatorType === 'editor'
        )

        const evalUser = evaluation.evaluateeName // tên người được đánh giá
        // Duyệt qua từng phòng ban trong departmentListV2
        departmentListV2.forEach(dept => {
          // Kiểm tra xem trong phòng ban có user nào có user_name trùng với evaluatee không
          const userFound = dept.users.find(user => user.user_name === evalUser)
          if (userFound) {
            // Nếu chưa có mapping cho phòng ban này thì khởi tạo
            if (!userDeptComments[dept.code]) {
              userDeptComments[dept.code] = {}
            }
            // Gán selfEvaluation từ self, manager, và editor, và comments từ editor cho ketQua
            userDeptComments[dept.code][evalUser] = {
              selfEvaluation: selfDetail
                ? stripHtml(selfDetail.selfEvaluation)
                : '',
              managerEvaluation: managerDetail
                ? stripHtml(managerDetail.selfEvaluation)
                : '',
              editorEvaluation: editorDetail
                ? stripHtml(editorDetail.selfEvaluation)
                : '',
              // Dùng editor.comments để xác định ketQua (tick mark)
              ketQua: editorDetail ? editorDetail.comments : '',
            }
          }
        })
      }
    })

    // Sau đó, xây dựng dữ liệu theo cấu trúc yêu cầu
    const combinedData = departmentListV2.map(dept => {
      const deptMapping = userDeptComments[dept.code] || {}
      return {
        phongBan: utils.getNameInUpperCase(dept.name),
        members: dept.users.map(user => {
          const userData = deptMapping[user.user_name] || {}
          // Kết hợp selfEvaluation, managerEvaluation, và editorEvaluation vào ghiChu
          const ghiChuParts = []
          if (userData.selfEvaluation) {
            ghiChuParts.push(`Tự đánh giá:\n${userData.selfEvaluation}`)
          }
          if (userData.managerEvaluation) {
            ghiChuParts.push(
              `Trưởng phòng/ban đánh giá:\n${userData.managerEvaluation}`
            )
          }
          if (userData.editorEvaluation) {
            ghiChuParts.push(
              `Ban biên tập đánh giá:\n${userData.editorEvaluation}`
            )
          }
          const ghiChu = ghiChuParts.join('\n\n') // Phân tách bằng hai dòng trống

          return {
            hoTen: utils.getNameInCapitalize(user.name_uppercase),
            chucVu: user.position_name,
            ketQua: userData.ketQua || '', // Dùng để xác định tick
            ghiChu: ghiChu || '', // Kết hợp cả 3 đánh giá
          }
        }),
      }
    })

    // 1. Khởi tạo workbook và worksheet
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Báo cáo')

    // 2. Khai báo 9 cột (A→I)
    worksheet.columns = [
      { key: 'stt', width: 7 }, // A
      { key: 'hoTen', width: 25 }, // B
      { key: 'chucVu', width: 30 }, // C
      { key: 'tot', width: 15 }, // D - Hoàn thành xuất sắc
      { key: 'kha', width: 15 }, // E - Hoàn thành tốt
      { key: 'tb', width: 15 }, // F - Hoàn thành nhiệm vụ
      { key: 'khong', width: 15 }, // G - Không hoàn thành
      { key: 'ghiChu', width: 80 }, // H - Để chứa nội dung dài
      { key: 'blank2', width: 5 }, // I
    ]

    // Thiết lập chiều cao hàng mặc định
    worksheet.properties.defaultRowHeight = 25

    // 3. Phần Header trên cùng (2 dòng đầu)
    // Dòng 1
    worksheet.mergeCells('A1:D1')
    const leftHeader1 = worksheet.getCell('A1')
    leftHeader1.value = 'UBND THÀNH PHỐ HÀ NỘI'
    leftHeader1.font = { name: 'Times New Roman', size: 12 }
    leftHeader1.alignment = { horizontal: 'center', vertical: 'middle' }

    worksheet.mergeCells('E1:I1')
    const rightHeader1 = worksheet.getCell('E1')
    rightHeader1.value = 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM'
    rightHeader1.font = { name: 'Times New Roman', bold: true, size: 12 }
    rightHeader1.alignment = { horizontal: 'center', vertical: 'middle' }

    // Dòng 2
    worksheet.mergeCells('A2:D2')
    const leftHeader2 = worksheet.getCell('A2')
    leftHeader2.value = 'BÁO KINH TẾ VÀ ĐÔ THỊ'
    leftHeader2.font = { name: 'Times New Roman', bold: true, size: 12 }
    leftHeader2.alignment = { horizontal: 'center', vertical: 'middle' }

    worksheet.mergeCells('E2:I2')
    const rightHeader2 = worksheet.getCell('E2')
    rightHeader2.value = 'Độc lập - Tự do - Hạnh phúc'
    rightHeader2.font = { name: 'Times New Roman', bold: true, size: 12 }
    rightHeader2.alignment = { horizontal: 'center', vertical: 'middle' }

    // Thêm dòng trống
    worksheet.addRow([])

    // 4. Tiêu đề chính (dòng 4)
    worksheet.mergeCells('A4:I4')
    const titleCell = worksheet.getCell('A4')
    titleCell.value = `TỔNG HỢP KẾT QUẢ CHẤT LƯỢNG CÔNG VIỆC THÁNG ${month}/${year}`
    titleCell.font = { name: 'Times New Roman', bold: true, size: 14 }
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' }
    worksheet.getRow(4).height = 30

    // Mô tả (dòng 5)
    worksheet.mergeCells('A5:I5')
    const descCell = worksheet.getCell('A5')
    descCell.value =
      'Căn cứ Biên bản họp của Hội đồng thi đua Khen thưởng ngày ..../..../20.... ' +
      `về việc bình xét và đánh giá kết quả chất lượng công việc tháng ${month}/${year}, như sau:`
    descCell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    }
    descCell.font = { name: 'Times New Roman', size: 12 }
    worksheet.getRow(5).height = 40

    // Thêm dòng trống
    worksheet.addRow([])

    // 5. Tiêu đề bảng - SỬA LẠI PHẦN HEADER
    // Tiêu đề đầy đủ cho phần kết quả
    const resultTitles = [
      'Hoàn thành xuất sắc nhiệm vụ',
      'Hoàn thành tốt nhiệm vụ',
      'Hoàn thành nhiệm vụ',
      'Không hoàn thành nhiệm vụ',
    ]

    // Cách 1: Tạo header 2 hàng với merge cell và hiển thị đầy đủ
    const headerRow1 = worksheet.addRow([
      'STT', // A
      'Họ và Tên', // B
      'Chức vụ, vị trí việc làm', // C
      `Kết quả chất lượng công việc tháng ${month}/${year}`, // D
      '', // E
      '', // F
      '', // G
      'Ghi chú', // H
      '', // I
    ])
    worksheet.mergeCells(`D${headerRow1.number}:G${headerRow1.number}`)
    worksheet.mergeCells(`H${headerRow1.number}:I${headerRow1.number}`)

    // Dòng tiêu đề phụ với tên đầy đủ
    const headerRow2 = worksheet.addRow([
      '', // A
      '', // B
      '', // C
      resultTitles[0], // D - Hoàn thành xuất sắc nhiệm vụ
      resultTitles[1], // E - Hoàn thành tốt nhiệm vụ
      resultTitles[2], // F - Hoàn thành nhiệm vụ
      resultTitles[3], // G - Không hoàn thành nhiệm vụ
      '', // H
      '', // I
    ])
    worksheet.mergeCells(`H${headerRow2.number}:I${headerRow2.number}`)

    // Đảm bảo hiển thị đầy đủ nội dung trong các ô header
    for (let i = 4; i <= 7; i++) {
      const cell = headerRow2.getCell(i)
      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
      }
      headerRow2.height = 60
    }

    // Định dạng chung cho 2 hàng tiêu đề
    ;[headerRow1, headerRow2].forEach(row => {
      row.height = 40
      row.eachCell(cell => {
        cell.font = { name: 'Times New Roman', bold: true, size: 11 }
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle',
          wrapText: true,
        }
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        }
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFB4C6E7' },
        }
      })
    })

    // 6. Đổ dữ liệu với số thứ tự liên tục và số La Mã cho phòng ban
    // Biến đếm từng loại kết quả
    let hoanThanhXuatSacCount = 0
    let hoanThanhTotCount = 0
    let hoanThanhNhiemVuCount = 0
    let khongHoanThanhCount = 0

    function convertToRoman(num) {
      const romanNumerals = [
        { value: 1000, symbol: 'M' },
        { value: 900, symbol: 'CM' },
        { value: 500, symbol: 'D' },
        { value: 400, symbol: 'CD' },
        { value: 100, symbol: 'C' },
        { value: 90, symbol: 'XC' },
        { value: 50, symbol: 'L' },
        { value: 40, symbol: 'XL' },
        { value: 10, symbol: 'X' },
        { value: 9, symbol: 'IX' },
        { value: 5, symbol: 'V' },
        { value: 4, symbol: 'IV' },
        { value: 1, symbol: 'I' },
      ]

      let result = ''
      for (let i = 0; i < romanNumerals.length; i++) {
        while (num >= romanNumerals[i].value) {
          result += romanNumerals[i].symbol
          num -= romanNumerals[i].value
        }
      }
      return result
    }

    let displayCount = 0 // Số thứ tự hiển thị liên tục cho mọi user
    let totalStaffCount = 0 // Chỉ tính những bản ghi có dữ liệu ketQua

    combinedData.forEach((item, deptIndex) => {
      // Thêm dòng phòng ban (như cũ)
      const banRow = worksheet.addRow([
        convertToRoman(deptIndex + 1), // Số La Mã
        item.phongBan,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ])
      worksheet.mergeCells(`B${banRow.number}:I${banRow.number}`)
      banRow.font = { name: 'Times New Roman', bold: true, size: 11 }
      banRow.height = 25
      banRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' }
      for (let col = 2; col <= 9; col++) {
        banRow.getCell(col).alignment = {
          horizontal: 'left',
          vertical: 'middle',
        }
      }
      banRow.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        }
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF' },
        }
      })

      // Duyệt qua từng user trong phòng ban
      item.members.forEach((member, idx) => {
        displayCount++ // luôn tăng số thứ tự hiển thị

        // Nếu có ketQua thì mới tăng biến đếm dùng cho tổng hợp
        if (member.ketQua) {
          totalStaffCount++
        }

        // Dùng displayCount để đánh số thứ tự hiển thị cho mọi user
        const row = worksheet.addRow([
          displayCount, // Số thứ tự hiển thị (liên tục từ 1 đến hết)
          member.hoTen,
          member.chucVu,
          '', // cột D
          '', // cột E
          '', // cột F
          '', // cột G
          member.ghiChu || '',
          '',
        ])

        // Xác định cột "tick" dựa trên ketQua
        if (member.ketQua === 'excellent') {
          row.getCell(4).value = 'X'
          hoanThanhXuatSacCount++
        } else if (member.ketQua === 'good') {
          row.getCell(5).value = 'X'
          hoanThanhTotCount++
        } else if (member.ketQua === 'completed') {
          row.getCell(6).value = 'X'
          hoanThanhNhiemVuCount++
        } else if (member.ketQua === 'notCompleted') {
          row.getCell(7).value = 'X'
          khongHoanThanhCount++
        }

        worksheet.mergeCells(`H${row.number}:I${row.number}`)
        const lineCount = (member.ghiChu.match(/\n/g) || []).length + 1
        row.height = Math.max(25, 20 * lineCount)

        row.eachCell((cell, colNumber) => {
          cell.alignment = {
            horizontal:
              colNumber === 2 || colNumber === 3 || colNumber === 8
                ? 'left'
                : 'center',
            vertical: 'middle',
            wrapText: true, // Đảm bảo wrapText được bật
          }
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          }
          cell.font = { name: 'Times New Roman', size: 11 }
        })
        // Căn trái cho các cột Họ tên (B), Chức vụ (C), Ghi chú (H)
        row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' }
        row.getCell(3).alignment = { horizontal: 'left', vertical: 'middle' }
        row.getCell(8).alignment = {
          horizontal: 'left',
          vertical: 'top',
          wrapText: true,
        }

        // Áp dụng màu nền xen kẽ cho từng phòng ban
        if (idx % 2 === 0) {
          row.eachCell(cell => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'ffffff' },
            }
          })
        }
      })
    })

    // 7. Thêm phần tổng hợp kết quả & chữ ký cuối

    // Tính toán tỷ lệ % (tránh chia 0)
    const calcPercent = count =>
      totalStaffCount ? Math.round((count / totalStaffCount) * 100) + '%' : '0%'

    // Dòng tổng số
    const summaryRow = worksheet.addRow([
      '', // A
      'Tổng số:', // B
      '', // C
      hoanThanhXuatSacCount, // D
      hoanThanhTotCount, // E
      hoanThanhNhiemVuCount, // F
      khongHoanThanhCount, // G
      '', // H
      '', // I
    ])
    worksheet.mergeCells(`B${summaryRow.number}:C${summaryRow.number}`)
    worksheet.mergeCells(`H${summaryRow.number}:I${summaryRow.number}`)
    summaryRow.height = 25
    summaryRow.eachCell(cell => {
      cell.alignment = { horizontal: 'center', vertical: 'middle' }
      cell.font = { name: 'Times New Roman', bold: true, size: 11 }
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      }
    })

    // Dòng tỷ lệ %
    const percentageRow = worksheet.addRow([
      '', // A
      `Tỷ lệ %:`, // B
      '', // C
      calcPercent(hoanThanhXuatSacCount), // D
      calcPercent(hoanThanhTotCount), // E
      calcPercent(hoanThanhNhiemVuCount), // F
      calcPercent(khongHoanThanhCount), // G
      '', // H
      '', // I
    ])
    worksheet.mergeCells(`B${percentageRow.number}:C${percentageRow.number}`)
    worksheet.mergeCells(`H${percentageRow.number}:I${percentageRow.number}`)
    percentageRow.height = 25
    percentageRow.eachCell(cell => {
      cell.alignment = { horizontal: 'center', vertical: 'middle' }
      cell.font = { name: 'Times New Roman', bold: true, size: 11 }
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      }
    })

    // Thêm dòng trống
    worksheet.addRow([])
    // 8. SỬA LẠI PHẦN FOOTER THEO MẪU

    // Tạo layout footer 2 cột
    // Nơi nhận: ở bên trái
    // Footer dòng 1
    const footerRow1 = worksheet.addRow([])
    worksheet.mergeCells(`A${footerRow1.number}:D${footerRow1.number}`)
    worksheet.mergeCells(`E${footerRow1.number}:I${footerRow1.number}`)
    const leftFooter1 = worksheet.getCell(`A${footerRow1.number}`)
    leftFooter1.value = 'Nơi nhận:'
    leftFooter1.font = { name: 'Times New Roman', bold: true, size: 12 }
    leftFooter1.alignment = { horizontal: 'left', vertical: 'middle' }
    const rightFooter1 = worksheet.getCell(`E${footerRow1.number}`)
    rightFooter1.value = 'TL/TỔNG BIÊN TẬP'
    rightFooter1.font = { name: 'Times New Roman', bold: true, size: 12 }
    rightFooter1.alignment = { horizontal: 'center', vertical: 'middle' }

    const footerRow2 = worksheet.addRow([])
    worksheet.mergeCells(`A${footerRow2.number}:D${footerRow2.number}`)
    worksheet.mergeCells(`E${footerRow2.number}:I${footerRow2.number}`)
    const leftFooter2 = worksheet.getCell(`A${footerRow2.number}`)
    leftFooter2.value = '- Ban Biên tập (để báo cáo);'
    leftFooter2.font = { name: 'Times New Roman', bold: false, size: 12 }
    leftFooter2.alignment = { horizontal: 'left', vertical: 'middle' }
    const rightFooter2 = worksheet.getCell(`E${footerRow2.number}`)
    rightFooter2.value = 'PHÓ CHÁNH VĂN PHÒNG'
    rightFooter2.font = { name: 'Times New Roman', bold: true, size: 12 }
    rightFooter2.alignment = { horizontal: 'center', vertical: 'middle' }

    const footerRow3 = worksheet.addRow([])
    worksheet.mergeCells(`A${footerRow3.number}:D${footerRow3.number}`)
    worksheet.mergeCells(`E${footerRow3.number}:I${footerRow3.number}`)
    const leftFooter3 = worksheet.getCell(`A${footerRow3.number}`)
    leftFooter3.value = '- Các phòng ban, đơn vị trực thuộc (để thực hiện);'
    leftFooter3.font = { name: 'Times New Roman', bold: false, size: 12 }
    leftFooter3.alignment = { horizontal: 'left', vertical: 'middle' }
    const rightFooter3 = worksheet.getCell(`E${footerRow3.number}`)
    rightFooter3.value = 'PHỤ TRÁCH'
    rightFooter3.font = { name: 'Times New Roman', bold: true, size: 12 }
    rightFooter3.alignment = { horizontal: 'center', vertical: 'middle' }

    const footerRow4 = worksheet.addRow([])
    worksheet.mergeCells(`A${footerRow4.number}:D${footerRow4.number}`)
    worksheet.mergeCells(`E${footerRow4.number}:I${footerRow4.number}`)
    const leftFooter4 = worksheet.getCell(`A${footerRow4.number}`)
    leftFooter4.value = '- Lưu VP.'
    leftFooter4.font = { name: 'Times New Roman', bold: false, size: 12 }
    leftFooter4.alignment = { horizontal: 'left', vertical: 'middle' }
    const rightFooter4 = worksheet.getCell(`E${footerRow4.number}`)
    rightFooter4.value = ''

    // 9. Xuất file Excel
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/octet-stream' })
    saveAs(blob, 'BaoCaoChatLuongCongViec.xlsx')
  }

  return handleExport()
}

export default InternalAssessmentExportExcel
